import React, { useState, useRef, useEffect } from "react";
import { Button, ButtonGroup, Form } from "react-bootstrap";
import { Stage, Layer, Text, Rect, Circle, Image as KonvaImage, Transformer, Group, Line, Ellipse, Arrow } from "react-konva";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../assets/css/AddDetailsTab.css";
import EventService from "../../services/EventService.js";
import { useEventStore } from "../../store/eventStore";
import { FiTrash2 } from "react-icons/fi";

const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";
const CANVAS_STATE_COMMENT_PREFIX = "CANVAS_STATE_V1:";

const AddDetailsTab = ({
  onBack,
  onNext,
  registerValidator,
}) => {
  // ✅ Use global campaignData from context
  const campaignData = useEventStore((state) => state.eventDetails);
  const textElements = useEventStore((state) => state.canvasData.textElements);
  const shapes = useEventStore((state) => state.canvasData.shapes);
  const canvasHydrated = useEventStore((state) => state.canvasData.hydrated);
  const updateEventDetails = useEventStore((state) => state.updateEventDetails);
  const replaceCanvasData = useEventStore((state) => state.replaceCanvasData);
  const setTextElements = useEventStore((state) => state.setTextElements);
  const setShapes = useEventStore((state) => state.setShapes);
  const markSaved = useEventStore((state) => state.markSaved);
  // ✅ Destructure shared data
  const {
    campaignId,
    campaignName,
    startDate,
    endDate,
    eventTime,
    description,
    formFields,
    eventPriceTagLine,
    eventPrice,
    templateId,
    eventTemplateId,
    templateCategory,
    templateImage,
    templateHtml,
    templateSummary,
    videoConferenceLink,
    isDraft,
  } = campaignData;
  // Utility to convert 24-hour time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "10:30 AM"; // default
    const [hoursStr, minutes] = time24.split(":");
    let hours = parseInt(hoursStr, 10);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const isBlackStrokeColor = (color = "") => {
    if (typeof color !== "string") return false;
    const normalized = color.replace(/\s+/g, "").toLowerCase();
    return (
      normalized === "black" ||
      normalized === "#000" ||
      normalized === "#000000" ||
      normalized === "rgb(0,0,0)" ||
      normalized === "rgba(0,0,0,1)"
    );
  };

  const encodeCanvasState = (state) => {
    try {
      const json = JSON.stringify(state);
      const bytes = new TextEncoder().encode(json);
      let binary = "";
      bytes.forEach((b) => {
        binary += String.fromCharCode(b);
      });
      return btoa(binary);
    } catch (error) {
      console.error("Failed to encode canvas state:", error);
      return "";
    }
  };

  const decodeCanvasState = (encoded) => {
    if (!encoded) return null;
    try {
      const binary = atob(encoded);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const json = new TextDecoder().decode(bytes);
      return JSON.parse(json);
    } catch (error) {
      console.error("Failed to decode canvas state:", error);
      return null;
    }
  };

  // ✅ Local states remain as-is
  const templateRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 500, height: 500 });
  const [imageReady, setImageReady] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [editModal, setEditModal] = useState({
    show: false,
    targetElement: null,
    value: "",
  });

  const previewScale = 0.4;

  // Konva states
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // Text formatting
  const [textFormat, setTextFormat] = useState({
    fontSize: 24,
    fontFamily: "Arial",
    fontStyle: "normal",
    textDecoration: "",
    fill: "#000000",
  });

  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const textInputRef = useRef(null);
  const previewTemplateRef = useRef(null);
  const [bgKonvaImage, setBgKonvaImage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const updateField = (key, value) => {
    updateEventDetails({ [key]: value });
    // Clear error message when user starts editing
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  const deleteField = (index) => {
    if (index < 2) return;

    if (!window.confirm("Remove this field?")) return;

    const updated = formFields.filter((_, i) => i !== index);
    updateField("formFields", updated);
  };

  // Preload image to get size and load for Konva
  useEffect(() => {
    const resolvedTemplateImage = templateImage
      ? (templateImage.startsWith("http://") ||
        templateImage.startsWith("https://") ||
        templateImage.startsWith("data:") ||
        templateImage.startsWith("blob:")
          ? templateImage
          : `${BASE_URL}${templateImage}`)
      : "";

    setImageReady(false);

    if (!resolvedTemplateImage) {
      setImageSize({ width: 500, height: 500 });
      setBgKonvaImage(null);
      setImageReady(true);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const width = img.naturalWidth || img.width || 500;
      const height = img.naturalHeight || img.height || 500;
      setImageSize({ width, height });
      setBgKonvaImage(img);
      setImageReady(true);
    };
    img.onerror = () => {
      // Fallback dimensions if image fails to load
      setImageSize({ width: 500, height: 500 });
      setBgKonvaImage(null);
      setImageReady(true);
    };
    img.src = resolvedTemplateImage;
  }, [templateImage]);

  // Parse existing HTML template to Konva elements
  useEffect(() => {
    if (canvasHydrated) return;
    if (!imageReady) return;
    if (!templateHtml || !templateHtml.trim()) {
      replaceCanvasData({ textElements: [], shapes: [], hydrated: true });
      return;
    }

    // Helper function to extract rotation from transform style
    const extractRotation = (transform) => {
      if (!transform) return 0;
      const match = transform.match(/rotate\(([^)]+)deg\)/);
      return match ? parseFloat(match[1]) : 0;
    };

    const parseBorderStyle = (
      borderValue,
      borderColorValue,
      fallbackColor,
      fallbackWidth = 2,
    ) => {
      let strokeColor = fallbackColor;
      let strokeWidth = fallbackWidth;

      if (typeof borderColorValue === "string" && borderColorValue.trim()) {
        strokeColor = borderColorValue.trim();
      }

      if (typeof borderValue === "string" && borderValue.trim()) {
        const colorMatch = borderValue.match(
          /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]+\)|hsla?\([^)]+\)/i,
        );
        if (colorMatch?.[0]) {
          strokeColor = colorMatch[0];
        }

        const widthMatch =
          borderValue.match(/(\d+(?:\.\d+)?)px/i) ||
          borderValue.match(/(\d+(?:\.\d+)?)/);
        if (widthMatch?.[1]) {
          const parsedWidth = parseFloat(widthMatch[1]);
          if (Number.isFinite(parsedWidth) && parsedWidth >= 0) {
            strokeWidth = parsedWidth;
          }
        }
      }

      return { strokeColor, strokeWidth };
    };

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(templateHtml, 'text/html');
      const container = doc.body.firstElementChild || doc.body;
      const encodedCanvasState =
        container.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state") ||
        doc.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state");

      const commentMatch = templateHtml.match(
        new RegExp(`<!--\\s*${CANVAS_STATE_COMMENT_PREFIX}([A-Za-z0-9+/=]+)\\s*-->`),
      );
      const encodedFromComment = commentMatch?.[1] || "";

      const decodedCanvasState =
        decodeCanvasState(encodedCanvasState) || decodeCanvasState(encodedFromComment);

      if (
        decodedCanvasState &&
        Array.isArray(decodedCanvasState.textElements) &&
        Array.isArray(decodedCanvasState.shapes)
      ) {
        const savedBaseWidth = Number(decodedCanvasState.baseWidth) || 0;
        const savedBaseHeight = Number(decodedCanvasState.baseHeight) || 0;
        if (savedBaseWidth > 0 && savedBaseHeight > 0) {
          const isDifferentCanvasSize =
            Math.abs(imageSize.width - savedBaseWidth) > 1 ||
            Math.abs(imageSize.height - savedBaseHeight) > 1;
          if (isDifferentCanvasSize) {
            setImageSize({ width: savedBaseWidth, height: savedBaseHeight });
          }
        }

        replaceCanvasData({
          textElements: decodedCanvasState.textElements,
          shapes: decodedCanvasState.shapes,
          hydrated: true,
        });
        return;
      }

      const resolveRootDimension = (rawValue, fallback) => {
        if (typeof rawValue !== "string") return fallback;

        const trimmed = rawValue.trim();
        if (!trimmed) return fallback;

        if (trimmed.endsWith("%")) {
          const percent = parseFloat(trimmed);
          if (Number.isFinite(percent) && percent > 0) {
            return (percent / 100) * fallback;
          }
          return fallback;
        }

        const absolute = parseFloat(trimmed);
        if (Number.isFinite(absolute) && absolute > 0) {
          return absolute;
        }

        return fallback;
      };

      const rootWidth = resolveRootDimension(container.style?.width || "", imageSize.width);
      const rootHeight = resolveRootDimension(container.style?.height || "", imageSize.height);
      const parseBaseWidth = rootWidth;
      const parseBaseHeight = rootHeight;

      if (
        Number.isFinite(rootWidth) &&
        Number.isFinite(rootHeight) &&
        rootWidth > 0 &&
        rootHeight > 0
      ) {
        const isDifferentCanvasSize =
          Math.abs(imageSize.width - rootWidth) > 1 ||
          Math.abs(imageSize.height - rootHeight) > 1;
        if (isDifferentCanvasSize) {
          setImageSize({ width: rootWidth, height: rootHeight });
        }
      }

      const elements = container.querySelectorAll('div[style*="position: absolute"]');

      const parsedTexts = [];
      const parsedShapes = [];

      elements.forEach((el, index) => {
        const style = el.style;
        const left = parseFloat(style.left) || 0;
        const top = parseFloat(style.top) || 0;
        const width = parseFloat(style.width) || 0;
        const height = parseFloat(style.height) || 0;

        // Check if it's a shape (has background-color and border-radius or specific dimensions)
        const bgColor = style.backgroundColor;
        const borderRadius = style.borderRadius;
        const border = style.border;
        const borderColor = style.borderColor;
        const hasSpan = el.querySelector('span') !== null;
        const displayFlex = style.display === 'flex';
        const alignItems = style.alignItems;
        const justifyContent = style.justifyContent;

        // Check for triangle or star (has clip-path: polygon)
        const clipPath = style.clipPath || style.webkitClipPath;
        const hasClipPath = clipPath && clipPath.includes('polygon');
        
        // Count polygon points to distinguish triangle (3 points) from star (10 points)
        let polygonPointCount = 0;
        if (hasClipPath) {
          const polygonMatch = clipPath.match(/polygon\(([^)]+)\)/);
          if (polygonMatch) {
            const pointsStr = polygonMatch[1];
            // Split by comma to count points, e.g. "50% 0%, 0% 100%, 100% 100%" = 3 points
            const pointsList = pointsStr.split(',').map(p => p.trim()).filter(p => p.length > 0);
            polygonPointCount = pointsList.length;
          }
        }
        const isTriangle = hasClipPath && polygonPointCount === 3;
        const isStar = hasClipPath && polygonPointCount >= 10;
        
        // Check for SVG elements (lines and arrows don't have background-color)
        const svgElement = el.querySelector('svg');
        const hasSVG = svgElement !== null;
        
        // It's a shape if:
        // 1. Has SVG element (line or arrow), OR
        // 2. Has background-color AND:
        //    - Is a circle (borderRadius === '50%'), OR
        //    - Is a triangle (has clip-path: polygon with 3 points), OR
        //    - Is a star (has clip-path: polygon with 10+ points), OR
        //    - Has width/height > 0 AND (has flex display OR has a span inside OR has no direct text content)
        const isShape = hasSVG || (bgColor && (
          borderRadius === '50%' ||
          isTriangle ||
          isStar ||
          (width > 0 && height > 0 && (displayFlex || alignItems === 'center' || justifyContent === 'center' || hasSpan || !el.textContent.trim()))
        ));

        if (isShape) {
          // It's a shape
          const span = el.querySelector('span');
          const text = span ? span.textContent : (el.textContent || '').trim();

          // Handle percentage-based positioning
          let x = left;
          let y = top;
          let w = width;
          let h = height;

          if (style.left.includes('%')) {
            x = (parseFloat(style.left) / 100) * parseBaseWidth;
          }
          if (style.top.includes('%')) {
            y = (parseFloat(style.top) / 100) * parseBaseHeight;
          }
          if (style.width.includes('%')) {
            w = (parseFloat(style.width) / 100) * parseBaseWidth;
          }
          if (style.height.includes('%')) {
            h = (parseFloat(style.height) / 100) * parseBaseHeight;
          }

          const widthIsPercent = style.width.includes('%');
          const heightIsPercent = style.height.includes('%');
          const widthPercentValue = parseFloat(style.width);
          const heightPercentValue = parseFloat(style.height);
          const hasEqualPercentDimensions =
            widthIsPercent &&
            heightIsPercent &&
            Number.isFinite(widthPercentValue) &&
            Number.isFinite(heightPercentValue) &&
            Math.abs(widthPercentValue - heightPercentValue) < 0.1;
          const isCircleShape =
            borderRadius === '50%' &&
            (Math.abs(w - h) < 5 || hasEqualPercentDimensions);

          // Check for SVG elements (line, arrow)
          // Note: Stars are saved with clip-path, not SVG, so they're handled separately
          if (svgElement) {
            const pathElement = svgElement.querySelector('path');
            if (pathElement) {
              const pathData = pathElement.getAttribute('d') || '';
              const viewBox = svgElement.getAttribute('viewBox');
              let viewBoxOffset = { x: 0, y: 0 };
              
              // Parse viewBox if present (format: "minX minY width height")
              if (viewBox) {
                const vbParts = viewBox.split(/\s+/).map(parseFloat);
                if (vbParts.length >= 2) {
                  viewBoxOffset.x = vbParts[0] || 0;
                  viewBoxOffset.y = vbParts[1] || 0;
                }
              }
              
              const points = [];
              
              // Parse path data to extract points (handles both M and L commands)
              const pathCommands = pathData.match(/[ML][\s,]*(-?\d+(?:\.\d+)?)[\s,]*(-?\d+(?:\.\d+)?)/g) || [];
              pathCommands.forEach(cmd => {
                const coords = cmd.match(/-?\d+(?:\.\d+)?/g);
                if (coords && coords.length >= 2) {
                  // Points in SVG are relative to viewBox origin, convert to relative to shape position
                  const px = parseFloat(coords[0]) - viewBoxOffset.x;
                  const py = parseFloat(coords[1]) - viewBoxOffset.y;
                  points.push(px, py);
                }
              });
              
              if (pathData.includes('Z') || pathData.includes('z')) {
                // Closed path - arrow (stars use clip-path, not SVG)
                // Arrow typically has 5 points (10 coordinates)
                if (points.length >= 8) {
                  // Points from SVG path are the original Konva points (relative to center)
                  // The viewBox offset (minX, minY) is just for SVG rendering
                  // So we can use the points directly, but we need to calculate the center correctly
                  
                  // The saved left/top is x + minX, y + minY where (x, y) is the center
                  // We need to extract minX, minY from the points to calculate the center
                  const minPX = Math.min(...points.filter((_, i) => i % 2 === 0));
                  const maxPX = Math.max(...points.filter((_, i) => i % 2 === 0));
                  const minPY = Math.min(...points.filter((_, i) => i % 2 === 1));
                  const maxPY = Math.max(...points.filter((_, i) => i % 2 === 1));
                  
                  // Calculate center position
                  // Saved: left = x + minPX, so x = left - minPX
                  // But we can also use: center = left + width/2 - (minPX + maxPX)/2
                  // Since points are relative to center, (minPX + maxPX)/2 should be close to 0
                  // So center ≈ left + width/2
                  const centerX = x + w / 2;
                  const centerY = y + h / 2;
                  
                  parsedShapes.push({
                    id: `arrow-${Date.now()}-${index}`,
                    x: centerX,
                    y: centerY,
                    points: points, // Points are already center-relative
                    fill: pathElement.getAttribute('fill') || '#f59e0b',
                    stroke: pathElement.getAttribute('stroke') || '#d97706',
                    strokeWidth: parseFloat(pathElement.getAttribute('stroke-width')) || 2,
                    draggable: true,
                    rotation: extractRotation(style.transform),
                  });
                }
              } else {
                // Open path - line
                // Line points are relative to the line's position (x, y)
                // The saved x, y is the top-left of the bounding box
                // We need to calculate the actual line position
                if (points.length >= 4) {
                  const minPX = Math.min(...points.filter((_, i) => i % 2 === 0));
                  const minPY = Math.min(...points.filter((_, i) => i % 2 === 1));
                  
                  // Adjust points to be relative to the line's actual position
                  const adjustedPoints = points.map((p, idx) => {
                    if (idx % 2 === 0) return p - minPX; // x
                    return p - minPY; // y
                  });
                  
                  parsedShapes.push({
                    id: `line-${Date.now()}-${index}`,
                    x: x + minPX, // Actual line start position
                    y: y + minPY, // Actual line start position
                    points: adjustedPoints,
                    fill: pathElement.getAttribute('fill') || '#000000',
                    stroke: pathElement.getAttribute('stroke') || '#000000',
                    strokeWidth: parseFloat(pathElement.getAttribute('stroke-width')) || 3,
                    draggable: true,
                    rotation: extractRotation(style.transform),
                  });
                }
              }
            }
          } else if (isCircleShape) {
            // Circle (border-radius 50% and width ≈ height)
            const parsedCircleDiameter = hasEqualPercentDimensions
              ? (widthPercentValue / 100) * parseBaseWidth
              : w;
            const radius = (parsedCircleDiameter / 2) || 50;
            const circleBorder = parseBorderStyle(border, borderColor, '#dc2626', 2);
            const hasBlackCircleStroke = isBlackStrokeColor(circleBorder.strokeColor);
            parsedShapes.push({
              id: `circle-${Date.now()}-${index}`,
              x: x + radius,
              y: y + radius,
              radius: radius,
              fill: bgColor || '#ef4444',
              stroke: hasBlackCircleStroke ? "transparent" : circleBorder.strokeColor,
              strokeWidth: hasBlackCircleStroke ? 0 : circleBorder.strokeWidth,
              draggable: true,
              text: text,
              textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
              textFontFamily: span ? span.style.fontFamily || 'Arial' : 'Arial',
              textFontStyle: span ? (span.style.fontStyle + ' ' + span.style.fontWeight).trim() || 'normal' : 'normal',
              textDecoration: span ? span.style.textDecoration || '' : '',
              textFill: span ? span.style.color || '#ffffff' : '#ffffff',
              rotation: extractRotation(style.transform),
            });
          } else if (borderRadius === '50%' && !isCircleShape) {
            const ellipseBorder = parseBorderStyle(border, borderColor, '#6d28d9', 2);
            // Ellipse (border-radius 50% but width ≠ height)
            parsedShapes.push({
              id: `ellipse-${Date.now()}-${index}`,
              x: x + w / 2,
              y: y + h / 2,
              radiusX: w / 2,
              radiusY: h / 2,
              fill: bgColor || '#8b5cf6',
              stroke: ellipseBorder.strokeColor,
              strokeWidth: ellipseBorder.strokeWidth,
              draggable: true,
              text: text,
              textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
              textFontFamily: span ? span.style.fontFamily || 'Arial' : 'Arial',
              textFontStyle: span ? (span.style.fontStyle + ' ' + span.style.fontWeight).trim() || 'normal' : 'normal',
              textDecoration: span ? span.style.textDecoration || '' : '',
              textFill: span ? span.style.color || '#ffffff' : '#ffffff',
              rotation: extractRotation(style.transform),
            });
          } else if (isStar) {
            // Star - parse polygon points from clip-path
            const polygonMatch = clipPath.match(/polygon\(([^)]+)\)/);
            let starPoints = [];
            let starCenterX = x + w / 2;
            let starCenterY = y + h / 2;
            
            if (polygonMatch) {
              const pointsStr = polygonMatch[1];
              // Extract percentage pairs - handle decimal points: "50.12%" or "0%"
              const percentPairs = pointsStr.match(/(\d+(?:\.\d{1,2})?)%[\s,]+(\d+(?:\.\d{1,2})?)%/g) || [];
              
              const pointsFromTopLeft = [];
              percentPairs.forEach(pair => {
                const match = pair.match(/(\d+(?:\.\d+)?)%[\s,]+(\d+(?:\.\d+)?)%/);
                if (match) {
                  const pxFromLeft = (parseFloat(match[1]) / 100) * w;
                  const pyFromTop = (parseFloat(match[2]) / 100) * h;
                  pointsFromTopLeft.push(pxFromLeft, pyFromTop);
                }
              });
              
              if (pointsFromTopLeft.length > 0) {
                // Find bounding box
                const xs = pointsFromTopLeft.filter((_, i) => i % 2 === 0);
                const ys = pointsFromTopLeft.filter((_, i) => i % 2 === 1);
                const minPX = Math.min(...xs);
                const maxPX = Math.max(...xs);
                const minPY = Math.min(...ys);
                const maxPY = Math.max(...ys);
                
                // Center of bounding box within the container
                const containerCenterX = (minPX + maxPX) / 2;
                const containerCenterY = (minPY + maxPY) / 2;
                
                // Absolute center position in the image
                starCenterX = x + containerCenterX;
                starCenterY = y + containerCenterY;
                
                // Convert to Konva center-relative coordinates
                for (let i = 0; i < pointsFromTopLeft.length; i += 2) {
                  starPoints.push(
                    pointsFromTopLeft[i] - containerCenterX,
                    pointsFromTopLeft[i + 1] - containerCenterY
                  );
                }
              }
            }
            
            // If we couldn't parse points, generate default star points
            if (starPoints.length === 0) {
              const outerRadius = Math.min(w, h) / 2;
              const innerRadius = outerRadius / 2;
              const numPoints = 5;
              for (let i = 0; i < numPoints * 2; i++) {
                const angle = (i * Math.PI) / numPoints;
                const radius = i % 2 === 0 ? outerRadius : innerRadius;
                starPoints.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
              }
            }
            
            // Use the calculated center position
            const centerX = starCenterX;
            const centerY = starCenterY;
            
            const starBorder = parseBorderStyle(border, borderColor, '#f59e0b', 2);
            
            parsedShapes.push({
              id: `star-${Date.now()}-${index}`,
              x: centerX,
              y: centerY,
              points: starPoints && starPoints.length > 0 ? starPoints : [],
              fill: bgColor || '#fbbf24',
              stroke: starBorder.strokeColor,
              strokeWidth: starBorder.strokeWidth,
              draggable: true,
              text: text,
              textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
              textFontFamily: span ? span.style.fontFamily || 'Arial' : 'Arial',
              textFontStyle: span ? (span.style.fontStyle + ' ' + span.style.fontWeight).trim() || 'normal' : 'normal',
              textDecoration: span ? span.style.textDecoration || '' : '',
              textFill: span ? span.style.color || '#ffffff' : '#ffffff',
              rotation: extractRotation(style.transform),
            });
          } else if (isTriangle) {
            // Triangle
            const size = Math.max(w, h) || 80;
            const triangleBorder = parseBorderStyle(border, borderColor, '#059669', 2);
            
            parsedShapes.push({
              id: `triangle-${Date.now()}-${index}`,
              x: x + size / 2,
              y: y + size / 2,
              size: size,
              fill: bgColor || '#10b981',
              stroke: triangleBorder.strokeColor,
              strokeWidth: triangleBorder.strokeWidth,
              draggable: true,
              text: text,
              textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
              textFontFamily: span ? span.style.fontFamily || 'Arial' : 'Arial',
              textFontStyle: span ? (span.style.fontStyle + ' ' + span.style.fontWeight).trim() || 'normal' : 'normal',
              textDecoration: span ? span.style.textDecoration || '' : '',
              textFill: span ? span.style.color || '#ffffff' : '#ffffff',
              rotation: extractRotation(style.transform),
            });
          } else {
            // Rectangle
            const rectBorder = parseBorderStyle(border, borderColor, '#1e40af', 2);
            parsedShapes.push({
              id: `rect-${Date.now()}-${index}`,
              x: x,
              y: y,
              width: w || 100,
              height: h || 100,
              fill: bgColor || '#3b82f6',
              stroke: rectBorder.strokeColor,
              strokeWidth: rectBorder.strokeWidth,
              draggable: true,
              text: text,
              textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
              textFontFamily: span ? span.style.fontFamily || 'Arial' : 'Arial',
              textFontStyle: span ? (span.style.fontStyle + ' ' + span.style.fontWeight).trim() || 'normal' : 'normal',
              textDecoration: span ? span.style.textDecoration || '' : '',
              textFill: span ? span.style.color || '#ffffff' : '#ffffff',
              rotation: extractRotation(style.transform),
            });
          }
        } else {
          // It's a text element
          const text = el.textContent || '';
          const fontSize = parseFloat(style.fontSize) || 24;
          const fontFamily = style.fontFamily || 'Arial';
          const fontStyle = (style.fontStyle + ' ' + style.fontWeight).trim() || 'normal';
          const textDecoration = style.textDecoration || '';
          const color = style.color || '#000000';

          // Handle percentage-based positioning
          let x = left;
          let y = top;
          if (style.left.includes('%')) {
            x = (parseFloat(style.left) / 100) * parseBaseWidth;
          }
          if (style.top.includes('%')) {
            y = (parseFloat(style.top) / 100) * parseBaseHeight;
          }

          parsedTexts.push({
            id: `text-${Date.now()}-${index}`,
            x: x,
            y: y,
            text: text,
            fontSize: fontSize,
            fontFamily: fontFamily,
            fontStyle: fontStyle,
            textDecoration: textDecoration,
            fill: color,
            draggable: true,
            rotation: extractRotation(style.transform),
          });
        }
      });

      replaceCanvasData({
        textElements: parsedTexts,
        shapes: parsedShapes,
        hydrated: true,
      });
    } catch (error) {
      console.error("Error parsing template HTML:", error);
    }
  }, [canvasHydrated, imageReady, imageSize.width, imageSize.height, replaceCanvasData, templateHtml]);

  useEffect(() => {
    if (!canvasHydrated || !shapes?.length) return;

    const hasLegacyBlackCircleStroke = shapes.some(
      (shape) =>
        shape.id?.startsWith("circle-") &&
        (shape.strokeWidth || 0) > 0 &&
        isBlackStrokeColor(shape.stroke),
    );

    if (!hasLegacyBlackCircleStroke) return;

    setShapes((prev) =>
      prev.map((shape) => {
        if (
          shape.id?.startsWith("circle-") &&
          (shape.strokeWidth || 0) > 0 &&
          isBlackStrokeColor(shape.stroke)
        ) {
          return { ...shape, stroke: "transparent", strokeWidth: 0 };
        }
        return shape;
      }),
    );
  }, [canvasHydrated, setShapes, shapes]);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "blockquote",
  ];

  // Helper: extract rotation degrees from a CSS transform string.
  // Computed styles often return "matrix(...)" or "matrix3d(...)" instead of
  // the original rotate() call, so we handle several formats.
  const extractRotationFromTransform = (transform) => {
    if (!transform || transform === "none") return 0;

    // direct rotate() or rotateZ()
    const rotateMatch = transform.match(/rotate(?:Z)?\(([-0-9.]+)deg\)/i);
    if (rotateMatch) {
      return parseFloat(rotateMatch[1]);
    }

    // matrix(a, b, c, d, e, f) -> angle = atan2(b, a)
    const matrixMatch = transform.match(/matrix\(([^)]+)\)/i);
    if (matrixMatch) {
      const parts = matrixMatch[1].split(',').map((v) => parseFloat(v));
      if (parts.length >= 2) {
        const [a, b] = parts;
        const angle = Math.atan2(b, a) * (180 / Math.PI);
        return angle;
      }
    }

    // matrix3d variant
    const matrix3dMatch = transform.match(/matrix3d\(([^)]+)\)/i);
    if (matrix3dMatch) {
      const parts = matrix3dMatch[1].split(',').map((v) => parseFloat(v));
      if (parts.length >= 6) {
        const [a, b] = parts;
        const angle = Math.atan2(b, a) * (180 / Math.PI);
        return angle;
      }
    }

    return 0;
  };

  const handleElementClick = (e) => {
    if (!templateRef.current) return;
    const clickableTags = [
      "H1",
      "H2",
      "H3",
      "H4",
      "P",
      "SPAN",
      "LABEL",
      "STRONG",
      "EM",
      "DIV",
      "A",
    ];
    let el = e.target;
    while (
      el &&
      el !== templateRef.current &&
      !clickableTags.includes(el.tagName)
    )
      el = el.parentElement;

    if (!el || el === templateRef.current) return;

    // find nearest ancestor (including self) that has a transform style
    let rotation = 0;
    let nodeWithTransform = el;
    while (nodeWithTransform && nodeWithTransform !== templateRef.current) {
      if (nodeWithTransform.style && nodeWithTransform.style.transform) {
        rotation = extractRotationFromTransform(nodeWithTransform.style.transform);
        break;
      }
      nodeWithTransform = nodeWithTransform.parentElement;
    }

    setEditModal({ show: true, targetElement: el, value: el.innerHTML || "", rotation });
  };

  const addCustomField = () =>
    updateField("formFields", [
      ...formFields,
      { label: "Custom Field", required: false },
    ]);

  const toggleFieldRequired = (idx) => {
    const updated = [...formFields];
    updated[idx].required = !updated[idx].required;
    updateField("formFields", updated);
  };

  const changeFieldLabel = (idx, label) => {
    const updated = [...formFields];
    updated[idx].label = label;
    updateField("formFields", updated);
  };

  // Konva interaction functions
  const handleStageClick = (e) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
      setEditingId(null);
      return;
    }

    const clickedNode = e.target;
    const parentGroup = clickedNode.getParent();
    let id = clickedNode.id();
    
    // If clicked on Rect or Circle inside a Group, use the Group's ID
    if (parentGroup && parentGroup.className === 'Group' && parentGroup.id()) {
      id = parentGroup.id();
    }
    
    // Also check if the clicked node itself is a Group
    if (clickedNode.className === 'Group' && clickedNode.id()) {
      id = clickedNode.id();
    }
    
    if (id) {
      setSelectedId(id);
    }
  };

  const startEditingShapeText = (shapeId) => {
    const shape = shapes.find((s) => s.id === shapeId);
    if (!shape) return;

    setEditingId(shapeId);
    setEditingText(shape.text || "");

    setTimeout(() => {
      if (!textInputRef.current || !stageRef.current) return;

      try {
        const stage = stageRef.current;
        const container = stage.container();
        if (!container) return;

        const groupNode = stage.findOne(`#${shapeId}`);
        if (!groupNode) return;

        const shapePosition = groupNode.absolutePosition();
        const stageBox = container.getBoundingClientRect();

        let textX, textY, textWidth, textHeight;

        if (shape.id.startsWith("rect-")) {
          textX = shapePosition.x + 5;
          textY = shapePosition.y + 5;
          textWidth = shape.width - 10;
          textHeight = shape.height - 10;
        } else if (shape.id.startsWith("circle-")) {
          textX = shapePosition.x - shape.radius + 5;
          textY = shapePosition.y - shape.radius + 5;
          textWidth = shape.radius * 2 - 10;
          textHeight = shape.radius * 2 - 10;
        } else if (shape.id.startsWith("ellipse-")) {
          textX = shapePosition.x - shape.radiusX + 5;
          textY = shapePosition.y - shape.radiusY + 5;
          textWidth = shape.radiusX * 2 - 10;
          textHeight = shape.radiusY * 2 - 10;
        } else if (shape.id.startsWith("triangle-")) {
          const size = shape.size || 80;
          textX = shapePosition.x - size / 2 + 5;
          textY = shapePosition.y - size / 2 + 5;
          textWidth = size - 10;
          textHeight = size - 10;
        } else if (shape.id.startsWith("star-")) {
          // Star bounding box (approximate)
          const maxRadius = Math.max(...shape.points.filter((_, i) => i % 2 === 0).map((p, i) => Math.abs(p)));
          textX = shapePosition.x - maxRadius + 5;
          textY = shapePosition.y - maxRadius + 5;
          textWidth = maxRadius * 2 - 10;
          textHeight = maxRadius * 2 - 10;
        } else {
          // Default for other shapes
          textX = shapePosition.x + 5;
          textY = shapePosition.y + 5;
          textWidth = 100;
          textHeight = 50;
        }

        const areaPosition = {
          x: stageBox.left + textX,
          y: stageBox.top + textY,
        };

        textInputRef.current.style.position = 'fixed';
        textInputRef.current.style.left = `${areaPosition.x}px`;
        textInputRef.current.style.top = `${areaPosition.y}px`;
        textInputRef.current.style.width = `${Math.max(50, textWidth)}px`;
        textInputRef.current.style.height = `${Math.max(20, textHeight)}px`;
        textInputRef.current.style.fontSize = `${shape.textFontSize || 24}px`;
        textInputRef.current.style.border = '2px solid #007bff';
        textInputRef.current.style.padding = '4px';
        textInputRef.current.style.margin = '0px';
        textInputRef.current.style.overflow = 'auto';
        textInputRef.current.style.background = 'rgba(255, 255, 255, 0.95)';
        textInputRef.current.style.outline = 'none';
        textInputRef.current.style.color = shape.textFill || '#000000';
        textInputRef.current.style.fontFamily = shape.textFontFamily || 'Arial';
        textInputRef.current.style.fontStyle = shape.textFontStyle || 'normal';
        textInputRef.current.style.textDecoration = shape.textDecoration || 'none';
        textInputRef.current.style.transform = `rotateZ(${groupNode.rotation() || 0}deg)`;
        textInputRef.current.style.zIndex = '10000';
        textInputRef.current.style.textAlign = 'center';
        textInputRef.current.style.display = 'block';
        textInputRef.current.style.whiteSpace = 'pre-wrap';
        textInputRef.current.style.wordWrap = 'break-word';
        textInputRef.current.style.resize = 'both';
        textInputRef.current.value = shape.text || "";
        textInputRef.current.focus();
        textInputRef.current.select();
      } catch (error) {
        console.error("Error setting up text input:", error);
      }
    }, 100);
  };

  const handleTextDblClick = (e) => {
    e.cancelBubble = true;
    if (e.evt) {
      e.evt.cancelBubble = true;
    }

    const node = e.target;
    const stage = node.getStage();
    if (!stage) return;

    const container = stage.container();
    if (!container) return;

    let shapeId = null;
    const clickedNode = node;
    const parentGroup = clickedNode.getParent();

    if (parentGroup && parentGroup.className === 'Group' && parentGroup.id()) {
      shapeId = parentGroup.id();
    } else {
      shapeId = clickedNode.id();
    }

    if (!shapeId) return;

    const shape = shapes.find((s) => s.id === shapeId);

    if (shape) {
      startEditingShapeText(shapeId);
      return;
    }

    const textNode = clickedNode;
    setEditingId(shapeId);
    setEditingText(textNode.text() || "");

    setTimeout(() => {
      if (!textInputRef.current) return;

      try {
        const textPosition = textNode.absolutePosition();
        const stageBox = container.getBoundingClientRect();

        const areaPosition = {
          x: stageBox.left + textPosition.x,
          y: stageBox.top + textPosition.y,
        };

        const padding = textNode.padding() || 0;
        const width = Math.max(50, textNode.width() - padding * 2);
        const height = Math.max(20, textNode.height() - padding * 2);

        textInputRef.current.style.position = 'fixed';
        textInputRef.current.style.left = `${areaPosition.x}px`;
        textInputRef.current.style.top = `${areaPosition.y}px`;
        textInputRef.current.style.width = `${width}px`;
        textInputRef.current.style.height = `${height}px`;
        textInputRef.current.style.fontSize = `${textNode.fontSize() || 24}px`;
        textInputRef.current.style.border = '2px solid #007bff';
        textInputRef.current.style.padding = '4px';
        textInputRef.current.style.margin = '0px';
        textInputRef.current.style.overflow = 'auto';
        textInputRef.current.style.background = 'rgba(255, 255, 255, 0.95)';
        textInputRef.current.style.outline = 'none';
        textInputRef.current.style.color = textNode.fill() || '#000000';
        textInputRef.current.style.fontFamily = textNode.fontFamily() || 'Arial';
        textInputRef.current.style.fontStyle = textNode.fontStyle() || 'normal';
        textInputRef.current.style.textDecoration = textNode.textDecoration() || 'none';
        textInputRef.current.style.transform = `rotateZ(${textNode.rotation() || 0}deg)`;
        textInputRef.current.style.zIndex = '10000';
        textInputRef.current.value = textNode.text() || "";
        textInputRef.current.focus();
        textInputRef.current.select();
      } catch (error) {
        console.error("Error setting up text input:", error);
      }
    }, 100);
  };

  const handleTextInputChange = () => {
    if (!textInputRef.current || !editingId) return;
    const id = editingId;

    const shape = shapes.find((s) => s.id === id);
    if (shape) {
      setShapes((prev) =>
        prev.map((el) => {
          if (el.id === id) {
            return { ...el, text: textInputRef.current.value };
          }
          return el;
        })
      );
      return;
    }

    setTextElements((prev) =>
      prev.map((el) => {
        if (el.id === id) {
          return { ...el, text: textInputRef.current.value };
        }
        return el;
      })
    );
  };

  const handleTextInputBlur = () => {
    setEditingId(null);
  };

  const addTextElement = () => {
    const newText = {
      id: `text-${Date.now()}`,
      x: 50,
      y: 50,
      text: "Double click to edit",
      fontSize: textFormat.fontSize,
      fontFamily: textFormat.fontFamily,
      fontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      fill: textFormat.fill,
      draggable: true,
    };
    setTextElements([...textElements, newText]);
    setSelectedId(newText.id);
  };

  const addRectangle = () => {
    const newRect = {
      id: `rect-${Date.now()}`,
      x: 50,
      y: 50,
      width: 100,
      height: 100,
      fill: "#3b82f6",
      stroke: "#1e40af",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
    };
    setShapes([...shapes, newRect]);
    setSelectedId(newRect.id);
  };

  const addCircle = () => {
    const newCircle = {
      id: `circle-${Date.now()}`,
      x: 100,
      y: 100,
      radius: 50,
      fill: "#ef4444",
      stroke: "#dc2626",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
    };
    setShapes([...shapes, newCircle]);
    setSelectedId(newCircle.id);
  };

  const addTriangle = () => {
    const size = 80; // Base size for triangle
    const newTriangle = {
      id: `triangle-${Date.now()}`,
      x: 100,
      y: 100,
      size: size, // Size represents the height/base of the triangle
      fill: "#10b981",
      stroke: "#059669",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
    };
    setShapes([...shapes, newTriangle]);
    setSelectedId(newTriangle.id);
  };

  const addEllipse = () => {
    const newEllipse = {
      id: `ellipse-${Date.now()}`,
      x: 100,
      y: 100,
      radiusX: 60,
      radiusY: 40,
      fill: "#8b5cf6",
      stroke: "#6d28d9",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
    };
    setShapes([...shapes, newEllipse]);
    setSelectedId(newEllipse.id);
  };

  const addLine = () => {
    const newLine = {
      id: `line-${Date.now()}`,
      x: 50,
      y: 50,
      points: [0, 0, 100, 50], // [x1, y1, x2, y2] relative to x, y
      fill: "#000000",
      stroke: "#000000",
      strokeWidth: 3,
      draggable: true,
    };
    setShapes([...shapes, newLine]);
    setSelectedId(newLine.id);
  };

  const addArrow = () => {
    const arrowLength = 80;
    const arrowHeadSize = 15;
    // Arrow points: line from start to end, then arrowhead triangle
    const newArrow = {
      id: `arrow-${Date.now()}`,
      x: 50,
      y: 50,
      points: [
        0, 0, // Start point
        arrowLength, 0, // End point (line)
        arrowLength - arrowHeadSize, -arrowHeadSize / 2, // Arrowhead point 1
        arrowLength, 0, // Back to end
        arrowLength - arrowHeadSize, arrowHeadSize / 2, // Arrowhead point 2
      ],
      fill: "#f59e0b",
      stroke: "#d97706",
      strokeWidth: 2,
      draggable: true,
    };
    setShapes([...shapes, newArrow]);
    setSelectedId(newArrow.id);
  };

  const addStar = () => {
    const outerRadius = 40;
    const innerRadius = 20;
    const numPoints = 5;
    const points = [];
    
    for (let i = 0; i < numPoints * 2; i++) {
      const angle = (i * Math.PI) / numPoints;
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      points.push(Math.cos(angle) * radius);
      points.push(Math.sin(angle) * radius);
    }
    
    const newStar = {
      id: `star-${Date.now()}`,
      x: 100,
      y: 100,
      points: points,
      fill: "#fbbf24",
      stroke: "#f59e0b",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
    };
    setShapes([...shapes, newStar]);
    setSelectedId(newStar.id);
  };

  const deleteSelected = () => {
    if (selectedId) {
      setTextElements((prev) => prev.filter((el) => el.id !== selectedId));
      setShapes((prev) => prev.filter((el) => el.id !== selectedId));
      setSelectedId(null);
    }
  };

  const updateSelectedFormat = (format) => {
    if (!selectedId) return;

    const isText = textElements.find((el) => el.id === selectedId);
    if (isText) {
      setTextElements((prev) =>
        prev.map((el) => {
          if (el.id === selectedId) {
            return { ...el, ...format };
          }
          return el;
        })
      );
      setTextFormat((prev) => ({ ...prev, ...format }));
      return;
    }

    const shape = shapes.find((el) => el.id === selectedId);
    if (shape) {
      const shapeFormat = {};
      Object.keys(format).forEach((key) => {
        if (key === 'fontSize') {
          shapeFormat.textFontSize = format.fontSize;
        } else if (key === 'fontFamily') {
          shapeFormat.textFontFamily = format.fontFamily;
        } else if (key === 'fontStyle') {
          shapeFormat.textFontStyle = format.fontStyle;
        } else if (key === 'textDecoration') {
          shapeFormat.textDecoration = format.textDecoration;
        } else if (key === 'fill') {
          shapeFormat.textFill = format.fill;
        } else if (key === 'shapeFill') {
          // Handle shape background/fill color separately
          shapeFormat.fill = format.shapeFill;
        } else {
          shapeFormat[key] = format[key];
        }
      });

      setShapes((prev) =>
        prev.map((el) => {
          if (el.id === selectedId) {
            return { ...el, ...shapeFormat };
          }
          return el;
        })
      );
    }
  };

  // Function to update shape background/fill color
  const updateShapeFillColor = (color) => {
    if (!selectedId) return;
    
    const shape = shapes.find((el) => el.id === selectedId);
    if (shape) {
      setShapes((prev) =>
        prev.map((el) => {
          if (el.id === selectedId) {
            // Line shapes use stroke instead of fill
            if (el.id.startsWith("line-")) {
              return { ...el, stroke: color };
            }
            // All other shapes use fill
            return { ...el, fill: color };
          }
          return el;
        })
      );
    }
  };

  const toggleBold = () => {
    const selectedText = textElements.find((el) => el.id === selectedId);
    const selectedShape = shapes.find((el) => el.id === selectedId);

    if (selectedText) {
      const newStyle = selectedText.fontStyle.includes("bold")
        ? selectedText.fontStyle.replace("bold", "").trim()
        : selectedText.fontStyle + " bold";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    } else if (selectedShape) {
      const currentStyle = selectedShape.textFontStyle || "normal";
      const newStyle = currentStyle.includes("bold")
        ? currentStyle.replace("bold", "").trim()
        : currentStyle + " bold";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    }
  };

  const toggleItalic = () => {
    const selectedText = textElements.find((el) => el.id === selectedId);
    const selectedShape = shapes.find((el) => el.id === selectedId);

    if (selectedText) {
      const newStyle = selectedText.fontStyle.includes("italic")
        ? selectedText.fontStyle.replace("italic", "").trim()
        : selectedText.fontStyle + " italic";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    } else if (selectedShape) {
      const currentStyle = selectedShape.textFontStyle || "normal";
      const newStyle = currentStyle.includes("italic")
        ? currentStyle.replace("italic", "").trim()
        : currentStyle + " italic";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    }
  };

  const toggleUnderline = () => {
    const selectedText = textElements.find((el) => el.id === selectedId);
    const selectedShape = shapes.find((el) => el.id === selectedId);

    if (selectedText) {
      const newDecoration = selectedText.textDecoration === "underline" ? "" : "underline";
      updateSelectedFormat({ textDecoration: newDecoration });
    } else if (selectedShape) {
      const newDecoration = selectedShape.textDecoration === "underline" ? "" : "underline";
      updateSelectedFormat({ textDecoration: newDecoration });
    }
  };

  // Update transformer when selection changes
  useEffect(() => {
    if (selectedId && transformerRef.current) {
      const stage = transformerRef.current.getStage();
      const selectedNode = stage.findOne(`#${selectedId}`);
      if (selectedNode) {
        // For shapes in Groups, attach transformer to the Group
        const nodeToTransform = selectedNode.getParent()?.className === 'Group'
          ? selectedNode.getParent()
          : selectedNode;
        transformerRef.current.nodes([nodeToTransform]);
        transformerRef.current.getLayer().batchDraw();
      }
    } else {
      transformerRef.current?.nodes([]);
    }
  }, [selectedId, textElements, shapes]);

  // Auto-adjust font size in preview template to prevent overflow
  useEffect(() => {
    if (!showPreview || !previewTemplateRef.current) return;
    
    const container = previewTemplateRef.current;
    const shapes = container.querySelectorAll('div[style*="position: absolute"]');
    
    shapes.forEach((shape) => {
      // Ensure shape container clips content and centers content
      if (!shape.style.overflow || shape.style.overflow === 'visible') {
        shape.style.overflow = 'hidden';
      }
      
      // Ensure shape container uses flexbox for centering
      shape.style.display = 'flex';
      shape.style.alignItems = 'center';
      shape.style.justifyContent = 'center';
      
      // Get shape dimensions
      const shapeRect = shape.getBoundingClientRect();
      const shapeWidth = shapeRect.width;
      const shapeHeight = shapeRect.height;
      
      // Ensure text spans don't overflow and auto-adjust font size
      const spans = shape.querySelectorAll('span');
      spans.forEach((span) => {
        span.style.overflow = 'hidden';
        span.style.textOverflow = 'ellipsis';
        span.style.maxWidth = '100%';
        span.style.maxHeight = '100%';
        span.style.display = 'block';
        span.style.wordWrap = 'break-word';
        span.style.wordBreak = 'break-word';
        span.style.textAlign = 'center';
        
        // Auto-adjust font size if text overflows
        if (shapeWidth > 0 && shapeHeight > 0) {
          let fontSize = parseFloat(span.style.fontSize) || parseFloat(window.getComputedStyle(span).fontSize) || 24;
          const originalFontSize = fontSize;
          const minFontSize = 8;
          const padding = 10; // Account for padding/margins
          const availableWidth = shapeWidth - padding;
          const availableHeight = shapeHeight - padding;
          
          // Create a temporary element to measure text with word wrap
          const tempSpan = span.cloneNode(true);
          tempSpan.style.visibility = 'hidden';
          tempSpan.style.position = 'absolute';
          tempSpan.style.top = '-9999px';
          tempSpan.style.left = '-9999px';
          tempSpan.style.width = `${availableWidth}px`;
          tempSpan.style.whiteSpace = 'normal';
          tempSpan.style.wordWrap = 'break-word';
          tempSpan.style.wordBreak = 'break-word';
          document.body.appendChild(tempSpan);
          
          // Reduce font size until text fits
          while (fontSize > minFontSize) {
            tempSpan.style.fontSize = `${fontSize}px`;
            const textHeight = tempSpan.scrollHeight;
            
            // Check if text fits within available height
            if (textHeight <= availableHeight) {
              break;
            }
            
            fontSize = Math.max(minFontSize, fontSize * 0.9); // Reduce by 10%
          }
          
          document.body.removeChild(tempSpan);
          
          // Apply the adjusted font size if it was reduced
          if (fontSize < originalFontSize) {
            span.style.fontSize = `${fontSize}px`;
          }
        }
      });
    });
  }, [showPreview, textElements, shapes, imageSize.width, imageSize.height]);

  // Convert Konva elements to HTML
  const convertKonvaToHTML = () => {
    const imageWidth = imageSize.width;
    const imageHeight = imageSize.height;
    const stage = stageRef.current;
    const encodedCanvasState = encodeCanvasState({
      version: 1,
      baseWidth: imageWidth,
      baseHeight: imageHeight,
      textElements,
      shapes,
    });
    const rootCanvasStateAttr = encodedCanvasState
      ? ` data-canvas-state="${encodedCanvasState}"`
      : "";

    let html = `<div${rootCanvasStateAttr} style="position: relative; width: ${imageWidth}px; height: ${imageHeight}px; margin: 0; padding: 0; box-sizing: border-box;">`;

    textElements.forEach((el) => {
      let x = el.x;
      let y = el.y;
      let rotation = el.rotation || 0;

      if (stage) {
        const node = stage.findOne(`#${el.id}`);
        if (node) {
          const pos = node.absolutePosition();
          x = pos.x;
          y = pos.y;
          rotation = node.rotation() || 0;
        }
      }

      const leftPercent = (x / imageWidth) * 100;
      const topPercent = (y / imageHeight) * 100;

      const style = `
        position: absolute;
        left: ${leftPercent}%;
        top: ${topPercent}%;
        font-size: ${el.fontSize}px;
        font-family: ${el.fontFamily};
        font-style: ${el.fontStyle.includes("italic") ? "italic" : "normal"};
        font-weight: ${el.fontStyle.includes("bold") ? "bold" : "normal"};
        text-decoration: ${el.textDecoration || "none"};
        color: ${el.fill};
        white-space: pre-wrap;
        word-wrap: break-word;
        transform: rotate(${rotation}deg);
        transform-origin: top left;
`;

      html += `<div style="${style}">${el.text}</div>`;
    });

    shapes.forEach((el) => {
      let x = el.x;
      let y = el.y;
      let width = el.width;
      let height = el.height;
      let radius = el.radius;
      let rotation = el.rotation || 0;

      if (stage) {
        const node = stage.findOne(`#${el.id}`);
        if (node) {
          const pos = node.absolutePosition();
          x = pos.x;
          y = pos.y;
          rotation = node.rotation() || 0;

          // Check if it's a Group node (most shapes are in Groups)
          if (node.className === 'Group' && typeof node.getChildren === 'function') {
            const children = node.getChildren();
            const shapeNode = children.find(child =>
              child.className === 'Rect' || child.className === 'Circle' || child.className === 'Line' || child.className === 'Ellipse'
            );

            if (shapeNode) {
              if (shapeNode.className === 'Rect') {
                width = shapeNode.width();
                height = shapeNode.height();
              } else if (shapeNode.className === 'Circle') {
                radius = shapeNode.radius();
              } else if (shapeNode.className === 'Ellipse') {
                // Ellipse dimensions are handled separately in ellipse conversion
              } else if (shapeNode.className === 'Line' && el.id.startsWith("triangle-")) {
                // For triangles, size is stored in the shape object
              }
            }
          } else if (node.className === 'Line' && el.id.startsWith("line-")) {
            // Line shapes are not in Groups, handle directly
            // Points are already stored in el.points
          }
        }
      }

      if (el.id.startsWith("rect-")) {
        const leftPercent = (x / imageWidth) * 100;
        const topPercent = (y / imageHeight) * 100;
        const widthPercent = (width / imageWidth) * 100;
        const heightPercent = (height / imageHeight) * 100;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          transform: rotate(${rotation}deg);
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("circle-")) {
        const leftPercent = ((x - radius) / imageWidth) * 100;
        const topPercent = ((y - radius) / imageHeight) * 100;
        const widthPercent = ((radius * 2) / imageWidth) * 100;
        const heightPercent = ((radius * 2) / imageHeight) * 100;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          border-radius: 50%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          transform: rotate(${rotation}deg);
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("triangle-")) {
        const size = el.size || 80;
        const leftPercent = ((x - size / 2) / imageWidth) * 100;
        const topPercent = ((y - size / 2) / imageHeight) * 100;
        const widthPercent = (size / imageWidth) * 100;
        const heightPercent = (size / imageHeight) * 100;

        // Create triangle using CSS clip-path
        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
          transform: rotate(${rotation}deg);
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
          margin-top: 20%;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("ellipse-")) {
        let radiusX = el.radiusX;
        let radiusY = el.radiusY;
        let ellipseRotation = rotation;
        
        if (stage) {
          const groupNode = stage.findOne(`#${el.id}`);
          if (groupNode && groupNode.className === 'Group' && typeof groupNode.findOne === 'function') {
            ellipseRotation = groupNode.rotation() || 0;
            const ellipseNode = groupNode.findOne("Ellipse");
            if (ellipseNode) {
              radiusX = ellipseNode.radiusX();
              radiusY = ellipseNode.radiusY();
            }
          }
        }
        
        const leftPercent = ((x - radiusX) / imageWidth) * 100;
        const topPercent = ((y - radiusY) / imageHeight) * 100;
        const widthPercent = ((radiusX * 2) / imageWidth) * 100;
        const heightPercent = ((radiusY * 2) / imageHeight) * 100;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          border-radius: 50%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          transform: rotate(${ellipseRotation}deg);
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("line-")) {
        // Convert line to SVG path
        let linePoints = el.points || [0, 0, 100, 50];
        let lineRotation = rotation;
        
        // Get actual points and rotation from Konva node if available
        if (stage) {
          const lineNode = stage.findOne(`#${el.id}`);
          if (lineNode && lineNode.className === 'Line') {
            linePoints = lineNode.points() || linePoints;
            lineRotation = lineNode.rotation() || 0;
          }
        }
        
        const startX = x + linePoints[0];
        const startY = y + linePoints[1];
        const endX = x + linePoints[2];
        const endY = y + linePoints[3];
        
        const leftPercent = (Math.min(startX, endX) / imageWidth) * 100;
        const topPercent = (Math.min(startY, endY) / imageHeight) * 100;
        const widthPercent = (Math.abs(endX - startX) / imageWidth) * 100;
        const heightPercent = (Math.abs(endY - startY) / imageHeight) * 100;
        
        const svgPath = `M ${linePoints[0]} ${linePoints[1]} L ${linePoints[2]} ${linePoints[3]}`;
        const svgWidth = Math.abs(linePoints[2] - linePoints[0]) || 100;
        const svgHeight = Math.abs(linePoints[3] - linePoints[1]) || 50;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          transform: rotate(${lineRotation}deg);
          transform-origin: top left;
        `;

        html += `<div style="${style}"><svg width="${svgWidth}" height="${svgHeight}" style="position: absolute; top: 0; left: 0; filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));"><path d="${svgPath}" stroke="${el.stroke}" stroke-width="${el.strokeWidth}" fill="none" /></svg></div>`;
      } else if (el.id.startsWith("arrow-")) {
        // Convert arrow to SVG
        let arrowPoints = el.points || [];
        let arrowRotation = rotation;
        
        // Get actual points and rotation from Konva Group if available
        if (stage) {
          const groupNode = stage.findOne(`#${el.id}`);
          if (groupNode && groupNode.className === 'Group') {
            arrowRotation = groupNode.rotation() || 0;
            const lineNode = groupNode.findOne("Line");
            if (lineNode) {
              arrowPoints = lineNode.points() || arrowPoints;
            }
          }
        }
        
        if (arrowPoints.length === 0) return;
        
        const maxX = Math.max(...arrowPoints.filter((_, i) => i % 2 === 0));
        const maxY = Math.max(...arrowPoints.filter((_, i) => i % 2 === 1));
        const minX = Math.min(...arrowPoints.filter((_, i) => i % 2 === 0));
        const minY = Math.min(...arrowPoints.filter((_, i) => i % 2 === 1));
        
        const leftPercent = ((x + minX) / imageWidth) * 100;
        const topPercent = ((y + minY) / imageHeight) * 100;
        const widthPercent = ((maxX - minX) / imageWidth) * 100;
        const heightPercent = ((maxY - minY) / imageHeight) * 100;
        
        // Generate SVG path from points array [x1, y1, x2, y2, ...]
        let pathData = '';
        for (let i = 0; i < arrowPoints.length; i += 2) {
          if (i === 0) {
            pathData += `M ${arrowPoints[i]} ${arrowPoints[i + 1]}`;
          } else {
            pathData += ` L ${arrowPoints[i]} ${arrowPoints[i + 1]}`;
          }
        }
        pathData += ' Z';

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          transform: rotate(${arrowRotation}deg);
          transform-origin: center center;
        `;

        html += `<div style="${style}"><svg width="${maxX - minX}" height="${maxY - minY}" viewBox="${minX} ${minY} ${maxX - minX} ${maxY - minY}" style="filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));"><path d="${pathData}" fill="${el.fill}" stroke="${el.stroke}" stroke-width="${el.strokeWidth}" /></svg></div>`;
      } else if (el.id.startsWith("star-")) {
        // Convert star to SVG
        let starPoints = el.points || [];
        let starRotation = rotation;
        
        // Get actual points and rotation from Konva Group if available
        if (stage) {
          const groupNode = stage.findOne(`#${el.id}`);
          if (groupNode && groupNode.className === 'Group') {
            starRotation = groupNode.rotation() || 0;
            const lineNode = groupNode.findOne("Line");
            if (lineNode) {
              starPoints = lineNode.points() || starPoints;
            }
          }
        }
        
        if (starPoints.length === 0) return;
        
        const maxX = Math.max(...starPoints.filter((_, i) => i % 2 === 0));
        const maxY = Math.max(...starPoints.filter((_, i) => i % 2 === 1));
        const minX = Math.min(...starPoints.filter((_, i) => i % 2 === 0));
        const minY = Math.min(...starPoints.filter((_, i) => i % 2 === 1));
        
        const leftPercent = ((x + minX) / imageWidth) * 100;
        const topPercent = ((y + minY) / imageHeight) * 100;
        const widthPercent = ((maxX - minX) / imageWidth) * 100;
        const heightPercent = ((maxY - minY) / imageHeight) * 100;
        
        // Generate SVG path from points array [x1, y1, x2, y2, ...]
        let pathData = '';
        for (let i = 0; i < starPoints.length; i += 2) {
          if (i === 0) {
            pathData += `M ${starPoints[i]} ${starPoints[i + 1]}`;
          } else {
            pathData += ` L ${starPoints[i]} ${starPoints[i + 1]}`;
          }
        }
        pathData += ' Z';

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          transform: rotate(${starRotation}deg);
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          clip-path: polygon(${starPoints.filter((_, i) => i % 2 === 0).map((px, idx) => {
            const py = starPoints[idx * 2 + 1];
            const relX = ((px - minX) / (maxX - minX || 1)) * 100;
            const relY = ((py - minY) / (maxY - minY || 1)) * 100;
            return `${relX.toFixed(2)}% ${relY.toFixed(2)}%`;
          }).join(', ')});
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      }
    });

    if (encodedCanvasState) {
      html += `<div style="display: none !important;" data-canvas-state="${encodedCanvasState}"></div>`;
      html += `<!--${CANVAS_STATE_COMMENT_PREFIX}${encodedCanvasState}-->`;
    }

    html += `</div>`;
    return html;
  };

  useEffect(() => {
    if (!canvasHydrated) return;
    const latestHtml = convertKonvaToHTML();
    if (latestHtml !== templateHtml) {
      updateEventDetails({ templateHtml: latestHtml });
    }
  }, [
    canvasHydrated,
    convertKonvaToHTML,
    imageSize.height,
    imageSize.width,
    shapes,
    templateHtml,
    textElements,
    updateEventDetails,
  ]);
  
  const validateForm = () => {
    const missingFields = [];

    if (!campaignName?.trim()) missingFields.push("Event Name");
    if (!description?.trim()) missingFields.push("Event Details");
    if (!startDate) missingFields.push("Start Date");
    if (!endDate) missingFields.push("End Date");
    if (!eventTime) missingFields.push("Event Time");
    if (!eventPriceTagLine?.trim()) missingFields.push("Price Tag Line");
    if (!eventPrice) missingFields.push("Event Price");

    // Validate form fields
    if (!formFields?.length) {
      missingFields.push("Form Fields");
    } else {
      formFields.forEach((f, i) => {
        if (!f.label?.trim()) {
          missingFields.push(`Form Field ${i + 1} label`);
        }
      });
    }

    if (missingFields.length > 0) {
      setErrorMessage(
        `Please fill in the following required fields:\n\n${missingFields.join(
          "\n",
        )}`,
      );
      return false;
    }

    setErrorMessage(null);
    return true;
  };

  const handleSave = async () => {
    // Collect missing fields (excluding meetingLink)
    const missingFields = [];
    if (!campaignName) missingFields.push("Event Name");
    if (!description) missingFields.push("Event Details");
    if (!startDate) missingFields.push("Start Date");
    if (!endDate) missingFields.push("End Date");
    if (!eventTime) missingFields.push("Event Time");
    if (!eventPriceTagLine) missingFields.push("Price Tag Line");
    if (!eventPrice) missingFields.push("Event Price");
    if (!formFields?.length) missingFields.push("Form Fields");

    // Stop execution if required data missing
    if (missingFields.length > 0) {
      setErrorMessage(
        `Please fill in the following required fields before saving:\n\n${missingFields.join(", ")}`,
      );
      return false;
    }

    setErrorMessage(null);

    const latestTemplateHtml = convertKonvaToHTML();
    updateEventDetails({ templateHtml: latestTemplateHtml });

    const payload = {
      eventId: campaignId,
      eventName: campaignName,
      eventDetails: description,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      isPreview: false,
      isDraft: isDraft,
      eventTime: formatTimeTo12Hour(eventTime) || "10:30 AM",
      eventPriceTagLine: eventPriceTagLine,
      eventEnterPrice: parseFloat(eventPrice) || 0,
      eventObjective: templateCategory,
      templateId: templateId,
      eventTemplateId: eventTemplateId,
      templateSummary: templateSummary,
      templateBody: latestTemplateHtml,
      templateImage: templateImage,
      eventInputs: formFields.map((field) => ({
        inputTitle: field.label,
        isRequire: field.required,
      })),
      meetingLink: videoConferenceLink, // optional
    };

    try {
      const response = await EventService.saveEvent(payload);
      if (response.data?.success) {
        const ev = response.data.data;
        updateEventDetails(
          {
            campaignId: ev?.id || campaignId,
            templateId: ev?.eventTemplate?.templateId || templateId,
            eventTemplateId: ev?.eventTemplate?.id || eventTemplateId,
            templateHtml: ev?.eventTemplate?.templateBody || latestTemplateHtml,
            templateSummary: ev?.eventTemplate?.templateSummary || templateSummary,
            templateCategory: templateCategory,
            templateImage: ev?.eventTemplate?.templateImage || templateImage,
            isDraft: ev?.isDraft ?? isDraft,
          },
          false,
        );
        markSaved();

        setErrorMessage(null);
        alert("Event saved successfully!");
        return true;
      } else {
        console.error("API Error:", response.data);
        setErrorMessage("Failed to save event. Please try again.");
        return false;
      }
    } catch (error) {
      console.error("Request failed:", error);
      setErrorMessage("An error occurred while saving the event. Please try again.");
      return false;
    }
  };

  useEffect(() => {
    if (registerValidator) {
      registerValidator(() => validateForm);
    }
  }, [campaignData, registerValidator]);

  return (
    <div className="campaign-editor">
      <div className="designer-section">
        {/* Campaign details */}
        <div className="campaign-row">
          <div className="field">
            <label>Event Name<span className="required-star">*</span></label>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => updateField("campaignName", e.target.value)}
              placeholder="Enter Event name"
            />
          </div>
          <div className="field">
            <label>Start Date<span className="required-star">*</span></label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => updateField("startDate", e.target.value)}
            />
          </div>
          <div className="field">
            <label>End Date<span className="required-star">*</span></label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => updateField("endDate", e.target.value)}
            />
          </div>
          <div className="field">
            <label>Event Time<span className="required-star">*</span></label>
            <input
              type="time"
              value={eventTime}
              onChange={(e) => updateField("eventTime", e.target.value)}
            />
          </div>
        </div>

        {/* Konva Template Editor */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ marginBottom: "10px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            {/* Add Text Button */}
            <button
              onClick={addTextElement}
              style={{
                padding: "8px 12px",
                border: "2px solid #007bff",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#007bff",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#007bff";
                e.target.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                e.target.style.color = "#007bff";
              }}
            >
              <span style={{ fontSize: "16px" }}>T</span>
              <span>Text</span>
            </button>

            {/* Add Rectangle Button */}
            <button
              onClick={addRectangle}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #3b82f6",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#3b82f6";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#3b82f6";
                }
              }}
              title="Add Rectangle"
            >
              <div
                className="shape-icon"
                style={{
                  width: "24px",
                  height: "18px",
                  border: "2px solid #3b82f6",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Circle Button */}
            <button
              onClick={addCircle}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #ef4444",
                borderRadius: "50%",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#ef4444";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#ef4444";
                }
              }}
              title="Add Circle"
            >
              <div
                className="shape-icon"
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #ef4444",
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Triangle Button */}
            <button
              onClick={addTriangle}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #10b981",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#10b981";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#10b981";
                }
              }}
              title="Add Triangle"
            >
              <svg
                width="20"
                height="18"
                viewBox="0 0 20 18"
                style={{ pointerEvents: "none" }}
              >
                <path
                  d="M10 0 L20 18 L0 18 Z"
                  fill="transparent"
                  stroke="#10b981"
                  strokeWidth="2"
                  style={{ transition: "all 0.2s" }}
                />
              </svg>
            </button>

            {/* Add Ellipse Button */}
            <button
              onClick={addEllipse}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #8b5cf6",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#8b5cf6";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#8b5cf6";
                }
              }}
              title="Add Ellipse"
            >
              <div
                className="shape-icon"
                style={{
                  width: "24px",
                  height: "16px",
                  border: "2px solid #8b5cf6",
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Line Button */}
            <button
              onClick={addLine}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #6b7280",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#6b7280";
                const line = e.target.querySelector('.line-icon');
                if (line) {
                  line.style.backgroundColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const line = e.target.querySelector('.line-icon');
                if (line) {
                  line.style.backgroundColor = "#6b7280";
                }
              }}
              title="Add Line"
            >
              <div
                className="line-icon"
                style={{
                  width: "20px",
                  height: "3px",
                  backgroundColor: "#6b7280",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Arrow Button */}
            <button
              onClick={addArrow}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #f59e0b",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f59e0b";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fff";
                  path.style.fill = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#f59e0b";
                  path.style.fill = "#f59e0b";
                }
              }}
              title="Add Arrow"
            >
              <svg
                width="20"
                height="18"
                viewBox="0 0 20 18"
                style={{ pointerEvents: "none" }}
              >
                <path
                  d="M0 9 L15 9 M15 9 L10 4 M15 9 L10 14"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{ transition: "all 0.2s" }}
                />
              </svg>
            </button>

            {/* Add Star Button */}
            <button
              onClick={addStar}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #fbbf24",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#fbbf24";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fff";
                  path.style.fill = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fbbf24";
                  path.style.fill = "#fbbf24";
                }
              }}
              title="Add Star"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                style={{ pointerEvents: "none" }}
              >
                <path
                  d="M10 0 L12.5 7.5 L20 7.5 L14 12.5 L16.5 20 L10 15 L3.5 20 L6 12.5 L0 7.5 L7.5 7.5 Z"
                  fill="#fbbf24"
                  stroke="#fbbf24"
                  strokeWidth="1"
                  style={{ transition: "all 0.2s" }}
                />
              </svg>
            </button>

            {/* Delete Button */}
            {selectedId && (
              <button
                onClick={deleteSelected}
                style={{
                  padding: "8px 12px",
                  border: "2px solid #dc3545",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#dc3545",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#dc3545";
                  e.target.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#fff";
                  e.target.style.color = "#dc3545";
                }}
              >
                <span>Delete</span>
              </button>
            )}
          </div>

          {/* Text Formatting Toolbar */}
          {(textElements.find((el) => el.id === selectedId) || shapes.find((el) => el.id === selectedId)) && (
            <div className="mb-3 p-2 border rounded" style={{ backgroundColor: "#f8f9fa" }}>
              {shapes.find((el) => el.id === selectedId) && (
                <div className="mb-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => startEditingShapeText(selectedId)}
                    className="me-2"
                  >
                    Edit Text
                  </Button>
                </div>
              )}

              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Button
                  variant={
                    ((textElements.find((el) => el.id === selectedId)?.fontStyle?.includes("bold")) ||
                      (shapes.find((el) => el.id === selectedId)?.textFontStyle?.includes("bold")))
                      ? "primary"
                      : "outline-secondary"
                  }
                  size="sm"
                  onClick={toggleBold}
                >
                  <strong>B</strong>
                </Button>
                <Button
                  variant={
                    ((textElements.find((el) => el.id === selectedId)?.fontStyle?.includes("italic")) ||
                      (shapes.find((el) => el.id === selectedId)?.textFontStyle?.includes("italic")))
                      ? "primary"
                      : "outline-secondary"
                  }
                  size="sm"
                  onClick={toggleItalic}
                >
                  <em>I</em>
                </Button>
                <Button
                  variant={
                    ((textElements.find((el) => el.id === selectedId)?.textDecoration === "underline") ||
                      (shapes.find((el) => el.id === selectedId)?.textDecoration === "underline"))
                      ? "primary"
                      : "outline-secondary"
                  }
                  size="sm"
                  onClick={toggleUnderline}
                >
                  <u>U</u>
                </Button>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2">
                <Form.Label className="mb-0">Size:</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  style={{ width: "80px" }}
                  value={
                    textElements.find((el) => el.id === selectedId)?.fontSize ||
                    shapes.find((el) => el.id === selectedId)?.textFontSize ||
                    textFormat.fontSize
                  }
                  onChange={(e) => updateSelectedFormat({ fontSize: parseInt(e.target.value) || 24 })}
                />

                <Form.Label className="mb-0">Font:</Form.Label>
                <Form.Select
                  size="sm"
                  style={{ width: "150px" }}
                  value={
                    textElements.find((el) => el.id === selectedId)?.fontFamily ||
                    shapes.find((el) => el.id === selectedId)?.textFontFamily ||
                    textFormat.fontFamily
                  }
                  onChange={(e) => updateSelectedFormat({ fontFamily: e.target.value })}
                >
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Comic Sans MS">Comic Sans MS</option>
                </Form.Select>

                <Form.Label className="mb-0">Color:</Form.Label>
                <Form.Control
                  type="color"
                  size="sm"
                  style={{ width: "60px", height: "31px" }}
                  value={
                    textElements.find((el) => el.id === selectedId)?.fill ||
                    shapes.find((el) => el.id === selectedId)?.textFill ||
                    textFormat.fill
                  }
                  onChange={(e) => updateSelectedFormat({ fill: e.target.value })}
                />
              </div>

              {/* Shape Background Color (only for shapes) */}
              {shapes.find((el) => el.id === selectedId) && (
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top">
                  <Form.Label className="mb-0">
                    {shapes.find((el) => el.id === selectedId)?.id.startsWith("line-") 
                      ? "Line Color:" 
                      : "Shape Color:"}
                  </Form.Label>
                  <Form.Control
                    type="color"
                    size="sm"
                    style={{ width: "60px", height: "31px" }}
                    value={
                      shapes.find((el) => el.id === selectedId)?.id.startsWith("line-")
                        ? (shapes.find((el) => el.id === selectedId)?.stroke || "#000000")
                        : (shapes.find((el) => el.id === selectedId)?.fill || "#3b82f6")
                    }
                    onChange={(e) => updateShapeFillColor(e.target.value)}
                  />
                  <span className="text-muted small">
                    {shapes.find((el) => el.id === selectedId)?.id.startsWith("line-")
                      ? (shapes.find((el) => el.id === selectedId)?.stroke || "#000000")
                      : (shapes.find((el) => el.id === selectedId)?.fill || "#3b82f6")}
                  </span>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: "200px", alignItems: "flex-start" }}>
            <div
              style={{
                position: "relative",
                display: "inline-block",
                border: "1px solid #ddd",
                backgroundColor: "#fff",
              }}
            >
            <Stage
              width={imageSize.width}
              height={imageSize.height}
              ref={stageRef}
              onClick={handleStageClick}
              onTap={handleStageClick}
            >
              <Layer>
                {/* Background Image */}
                {bgKonvaImage && (
                  <KonvaImage
                    x={0}
                    y={0}
                    width={imageSize.width}
                    height={imageSize.height}
                    image={bgKonvaImage}
                  />
                )}

                {/* Text Elements */}
                {textElements.map((el) => (
                  <Text
                    key={el.id}
                    id={el.id}
                    x={el.x}
                    y={el.y}
                    text={el.text}
                    fontSize={el.fontSize}
                    fontFamily={el.fontFamily}
                    fontStyle={el.fontStyle}
                    textDecoration={el.textDecoration}
                    fill={el.fill}
                    rotation={el.rotation}
                    draggable
                    onDblClick={handleTextDblClick}
                    onDblTap={handleTextDblClick}
                    onDragEnd={(e) => {
                      setTextElements((prev) =>
                        prev.map((item) =>
                          item.id === el.id
                            ? { ...item, x: e.target.x(), y: e.target.y() }
                            : item
                        )
                      );
                    }}
                    onTransformEnd={(e) => {
                      const node = e.target;
                      const scaleX = node.scaleX();
                      const scaleY = node.scaleY();

                      setTextElements((prev) =>
                        prev.map((item) =>
                          item.id === el.id
                            ? {
                              ...item,
                              x: node.x(),
                              y: node.y(),
                              fontSize: Math.max(5, item.fontSize * scaleY),
                              rotation: node.rotation(),
                            }
                            : item
                        )
                      );

                      node.scaleX(1);
                      node.scaleY(1);
                    }}
                  />

                ))}

                {/* Shapes */}
                {shapes.map((el) => {
                  if (el.id.startsWith("rect-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation || 0}
                        draggable={el.draggable}
                        onClick={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onTap={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onDblClick={handleTextDblClick}
                        onDblTap={handleTextDblClick}
                        onDragEnd={(e) => {
                          const group = e.target;
                          setShapes((prev) =>
                            prev.map((item) =>
                              item.id === el.id
                                ? { ...item, x: group.x(), y: group.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const group = e.target;
                          const scaleX = group.scaleX();
                          const scaleY = group.scaleY();

                          const rectNode = group.findOne("Rect");

                          setShapes((prev) =>
                            prev.map((item) => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: group.x(),
                                  y: group.y(),
                                  width: Math.max(5, rectNode.width() * scaleX),
                                  height: Math.max(5, rectNode.height() * scaleY),
                                  rotation: group.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          group.scaleX(1);
                          group.scaleY(1);
                        }}
                      >
                        <Rect
                          width={el.width}
                          height={el.height}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          onClick={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onTap={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                        {el.text && (
                          <Text
                            text={el.text}
                            width={el.width}
                            height={el.height}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>
                    );
                  } else if (el.id.startsWith("circle-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation || 0}
                        draggable={el.draggable}
                        onClick={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onTap={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onDblClick={handleTextDblClick}
                        onDblTap={handleTextDblClick}
                        onDragEnd={(e) => {
                          const group = e.target;
                          setShapes((prev) =>
                            prev.map((item) =>
                              item.id === el.id
                                ? { ...item, x: group.x(), y: group.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const group = e.target;
                          const scaleX = group.scaleX();
                          const scaleY = group.scaleY();

                          const circleNode = group.findOne("Circle");
                          const newRadius = circleNode.radius() * Math.max(scaleX, scaleY);

                          setShapes((prev) =>
                            prev.map((item) => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: group.x(),
                                  y: group.y(),
                                  radius: Math.max(5, newRadius),
                                  rotation: group.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          group.scaleX(1);
                          group.scaleY(1);
                        }}
                      >
                        <Circle
                          radius={el.radius}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          onClick={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onTap={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                        {el.text && (
                          <Text
                            text={el.text}
                            x={-el.radius}
                            y={-el.radius}
                            width={el.radius * 2}
                            height={el.radius * 2}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>
                    );
                  } else if (el.id.startsWith("triangle-")) {
                    const size = el.size || 80;
                    // Triangle points: top, bottom-left, bottom-right
                    const points = [
                      0, -size / 2,        // Top point
                      -size / 2, size / 2, // Bottom left
                      size / 2, size / 2   // Bottom right
                    ];
                    
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation || 0}
                        draggable={el.draggable}
                        onClick={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onTap={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onDblClick={handleTextDblClick}
                        onDblTap={handleTextDblClick}
                        onDragEnd={(e) => {
                          const group = e.target;
                          setShapes((prev) =>
                            prev.map((item) =>
                              item.id === el.id
                                ? { ...item, x: group.x(), y: group.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const group = e.target;
                          const scaleX = group.scaleX();
                          const scaleY = group.scaleY();
                          const scale = Math.max(scaleX, scaleY);

                          setShapes((prev) =>
                            prev.map((item) => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: group.x(),
                                  y: group.y(),
                                  size: Math.max(10, el.size * scale),
                                  rotation: group.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          group.scaleX(1);
                          group.scaleY(1);
                        }}
                      >
                        <Line
                          points={points}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          closed={true}
                          onClick={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onTap={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                        {el.text && (
                          <Text
                            text={el.text}
                            x={-size / 2}
                            y={-size / 4}
                            width={size}
                            height={size * 0.75}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>
                    );
                  } else if (el.id.startsWith("ellipse-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation || 0}
                        draggable={el.draggable}
                        onClick={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onTap={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onDblClick={handleTextDblClick}
                        onDblTap={handleTextDblClick}
                        onDragEnd={(e) => {
                          const group = e.target;
                          setShapes((prev) =>
                            prev.map((item) =>
                              item.id === el.id
                                ? { ...item, x: group.x(), y: group.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const group = e.target;
                          const scaleX = group.scaleX();
                          const scaleY = group.scaleY();

                          const ellipseNode = group.findOne("Ellipse");

                          setShapes((prev) =>
                            prev.map((item) => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: group.x(),
                                  y: group.y(),
                                  radiusX: Math.max(5, ellipseNode.radiusX() * scaleX),
                                  radiusY: Math.max(5, ellipseNode.radiusY() * scaleY),
                                  rotation: group.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          group.scaleX(1);
                          group.scaleY(1);
                        }}
                      >
                        <Ellipse
                          radiusX={el.radiusX}
                          radiusY={el.radiusY}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          onClick={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onTap={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                        {el.text && (
                          <Text
                            text={el.text}
                            x={-el.radiusX}
                            y={-el.radiusY}
                            width={el.radiusX * 2}
                            height={el.radiusY * 2}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>
                    );
                  } else if (el.id.startsWith("line-")) {
                    return (
                      <Line
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        points={el.points}
                        stroke={el.stroke}
                        strokeWidth={el.strokeWidth}
                        draggable={el.draggable}
                        onClick={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onTap={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onDragEnd={(e) => {
                          setShapes((prev) =>
                            prev.map((item) =>
                              item.id === el.id
                                ? { ...item, x: e.target.x(), y: e.target.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scaleX = node.scaleX();
                          const scaleY = node.scaleY();

                          setShapes((prev) =>
                            prev.map((item) => {
                              if (item.id === el.id) {
                                const newPoints = item.points.map((p, idx) => {
                                  if (idx % 2 === 0) return p * scaleX; // x
                                  return p * scaleY; // y
                                });
                                return {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  points: newPoints,
                                  rotation: node.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      />
                    );
                  } else if (el.id.startsWith("arrow-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation || 0}
                        draggable={el.draggable}
                        onClick={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onTap={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onDragEnd={(e) => {
                          const group = e.target;
                          setShapes((prev) =>
                            prev.map((item) =>
                              item.id === el.id
                                ? { ...item, x: group.x(), y: group.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const group = e.target;
                          const scaleX = group.scaleX();
                          const scaleY = group.scaleY();
                          const scale = Math.max(scaleX, scaleY);

                          setShapes((prev) =>
                            prev.map((item) => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: group.x(),
                                  y: group.y(),
                                  points: item.points.map(p => p * scale),
                                  rotation: group.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          group.scaleX(1);
                          group.scaleY(1);
                        }}
                      >
                        <Line
                          points={el.points}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          closed={true}
                          onClick={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onTap={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                        />
                      </Group>
                    );
                  } else if (el.id.startsWith("star-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation || 0}
                        draggable={el.draggable}
                        onClick={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onTap={(e) => {
                          e.cancelBubble = true;
                          setSelectedId(el.id);
                        }}
                        onDblClick={handleTextDblClick}
                        onDblTap={handleTextDblClick}
                        onDragEnd={(e) => {
                          const group = e.target;
                          setShapes((prev) =>
                            prev.map((item) =>
                              item.id === el.id
                                ? { ...item, x: group.x(), y: group.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const group = e.target;
                          const scaleX = group.scaleX();
                          const scaleY = group.scaleY();
                          const scale = Math.max(scaleX, scaleY);

                          setShapes((prev) =>
                            prev.map((item) => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: group.x(),
                                  y: group.y(),
                                  points: item.points.map(p => p * scale),
                                  rotation: group.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          group.scaleX(1);
                          group.scaleY(1);
                        }}
                      >
                        <Line
                          points={el.points}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          closed={true}
                          onClick={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onTap={(e) => {
                            e.cancelBubble = true;
                            const group = e.target.getParent();
                            if (group && group.id()) {
                              setSelectedId(group.id());
                            }
                          }}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                        {el.text && (
                          <Text
                            text={el.text}
                            x={-40}
                            y={-40}
                            width={80}
                            height={80}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>
                    );
                  }
                  return null;
                })}

                {/* Transformer for resizing/rotating */}
                <Transformer
                  ref={transformerRef}
                  rotateEnabled
                  enabledAnchors={[
                    "top-left",
                    "top-right",
                    "bottom-left",
                    "bottom-right",
                  ]}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < 5 || newBox.height < 5) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>

            {/* Text input for editing */}
            <textarea
              ref={textInputRef}
              defaultValue={editingText}
              onInput={handleTextInputChange}
              onBlur={handleTextInputBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleTextInputBlur();
                }
                if (e.key === "Escape") {
                  handleTextInputBlur();
                }
              }}
              style={{
                position: "absolute",
                display: editingId ? "block" : "none",
              }}
            />

          </div>

            {/* Error Message Display - Right Side */}
            {errorMessage && (
              <div
                style={{
                  flex: "0 0 auto",
                  width: "350px",
                  padding: "20px 24px",
                  backgroundColor: "#fee",
                  border: "2px solid #fcc",
                  borderRadius: "8px",
                  color: "#c33",
                  whiteSpace: "pre-line",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  maxHeight: "600px",
                  overflow: "auto",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <span style={{ fontSize: "24px", fontWeight: "bold", flexShrink: 0 }}>⚠</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ display: "block", marginBottom: "8px", fontSize: "16px" }}>
                      Validation Error
                    </strong>
                    {errorMessage}
                  </div>
                  <button
                    onClick={() => setErrorMessage(null)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "24px",
                      cursor: "pointer",
                      color: "#c33",
                      padding: "0",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      lineHeight: "1",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#fcc";
                      e.target.style.borderRadius = "4px";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                    }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview toggle */}
        <div className="preview-toggle">
          <label className="toggle-switch small">
            <input
              type="checkbox"
              checked={showPreview}
              onChange={() => setShowPreview(!showPreview)}
            />
            <span className="slider" />
          </label>
          <span className="required-text">Show Preview</span>
        </div>

        {/* Description editor */}
        <div className="description-editor">
          <label>Description<span className="required-star">*</span></label>
          <ReactQuill
            value={description}
            onChange={(val) => updateField("description", val)}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>

        {/* Labels */}
        <div className="extra-labels-row">
          <input
            type="text"
            placeholder="Enter Campaign Registration tag line here*"
            className="large-label"
            value={eventPriceTagLine}
            onChange={(e) => updateField("eventPriceTagLine", e.target.value)}
          />
          <input
            type="text"
            placeholder="Registration*"
            className="small-label"
            value={eventPrice}
            onChange={(e) => updateField("eventPrice", e.target.value)}
          />
        </div>

        {/* Form builder */}
        <div className="form-builder">
          <h4>Form Fields</h4>
          {formFields.map((field, idx) => (
            <div key={idx} className="form-field">
              <div className="form-field-left">
                <input
                  className="wide-input"
                  type="text"
                  value={field.label}
                  onChange={(e) => changeFieldLabel(idx, e.target.value)}
                />
              </div>
              <div className="form-field-right">
                <label className="toggle-switch small">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={() => toggleFieldRequired(idx)}
                  />
                  <span className="slider" />
                </label>
                <span className="required-text">Required</span>

                {/* DELETE BUTTON */}
                <button
                  type="button"
                  className="delete-btn"
                  onClick={() => deleteField(idx)}
                  disabled={idx < 4}
                  title={
                    idx < 4
                      ? "Default fields cannot be deleted"
                      : "Delete field"
                  }
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
          <div className="form-builder-actions">
            <button className="btn primary" onClick={addCustomField}>
              Add Custom Field
            </button>
          </div>
        </div>

        {/* Bottom action buttons */}
        <div className="action-buttons">
          <button className="btn secondary go-back" onClick={onBack}>
            Back
          </button>
          <div className="right-actions">
            <button className="btn warn save" onClick={handleSave} disabled={!isDraft}>
              Save as Draft
            </button>
            <button
              className="btn success continue"
              onClick={async () => {
                if (validateForm()) {
                  const saveSuccess = await handleSave();
                  if (saveSuccess) {
                    onNext();
                  }
                }
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      {/* --- Preview Modal and Edit Modal remain unchanged --- */}
      {showPreview && (
        <div className="preview-overlay">
          <div className="preview-modal">
            <button
              className="close-preview"
              onClick={() => setShowPreview(false)}
            >
              ✕
            </button>

            <div
              className="preview-scale-container"
              style={{
                transform: `scale(${previewScale})`,
                transformOrigin: "top left",
                width: imageSize.width,
              }}
            >
              <div
                className="preview-template-wrapper"
                style={{
                  width: imageSize.width,
                  height: imageSize.height,
                  overflow: "hidden",
                  borderRadius: "12px",
                  boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px;",
                }}
              >
                <div
                  ref={previewTemplateRef}
                  className="preview-template"
                  dangerouslySetInnerHTML={{ __html: convertKonvaToHTML() }}
                  style={{
                    backgroundImage: `url(${templateImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    width: imageSize.width,
                    height: imageSize.height,
                    borderRadius: "12px",
                  }}
                />
              </div>

              <br />

              <div
                className="preview-description"
                dangerouslySetInnerHTML={{ __html: description }}
                style={{
                  width: imageSize.width,
                }}
              />

              <div
                className="preview-extra-labels"
                style={{
                  width: imageSize.width,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start", // 👈 align to left
                  gap: "8px",
                  flexWrap: "nowrap",
                }}
              >
                {eventPriceTagLine && (
                  <span
                    className="large-label-preview"
                    style={{ display: "inline-block" }}
                  >
                    {eventPriceTagLine}
                  </span>
                )}
                {eventPrice && (
                  <span
                    className="small-label-preview"
                    style={{ color: "red", display: "inline-block" }}
                  >
                    ₹ {eventPrice}
                  </span>
                )}
              </div>

              <br />

              <div
                className="preview-form"
                style={{
                  width: imageSize.width,
                }}
              >
                {formFields.map((field, idx) => (
                  <div key={idx} className="preview-form-row">
                    <label>
                      {field.label} {field.required ? "*" : ""}
                    </label>
                    <input type="text" placeholder={field.label} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {editModal.show && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <h3 style={{ paddingBottom: "5px" }}>Edit Content</h3>
            <div
              className="quill-container-wrapper"
              style={{ transform: `rotate(${editModal.rotation || 0}deg)`, transformOrigin: 'center left' }}
            >
              <ReactQuill
                value={editModal.value}
                onChange={(val) =>
                  setEditModal((prev) => ({ ...prev, value: val }))
                }
                modules={quillModules}
                formats={quillFormats}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn secondary"
                onClick={() =>
                  setEditModal({ show: false, targetElement: null, value: "" })
                }
              >
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={() => {
                  if (editModal.targetElement) {
                    editModal.targetElement.innerHTML = editModal.value;
                    updateField("templateHtml", templateRef.current.innerHTML);
                  }
                  setEditModal({ show: false, targetElement: null, value: "" });
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDetailsTab;



